import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BaseDto } from '@models/base/baseDto.model';
import { CreateAsuntoRequest } from '@models/asunto/createAsuntoRequest.model';
import { UpdateAsuntoRequest } from '@models/asunto/updateAsuntoRequest.model';
import { isFalsy } from 'utility-types';
import { CreateResponseBase } from '@models/base/createResponseBase.model';
import { FiltroAccesosRapidosRequest, AccesosRapidosPaginadosDto, AccesoRapidoSimpleDto, ActualizarFavoritoRequest, EstadisticasAccesosRapidosDto, AccesoMasUsadoDto, ActualizarOrdenRequest, VerificarUrlResponse, VerificacionMasivaDto, ConfiguracionAccesosRapidosDto, NotificacionAccesoRapidoDto, GrupoConEstadisticasDto, OptimizacionResultadoDto, SugerenciaAccesoRapidoDto, SugerenciaReorganizacionDto, ExportarAccesosRapidosRequest, ExportacionResultadoDto, ImportarAccesosRapidosRequest, ResultadoImportacionDto, SincronizacionResultadoDto, LimpiezaConfiguracionDto, LimpiezaResultadoDto, OptimizacionDto, BackupResultadoDto, ReporteUsoDto, AnalisisPatronesDto, MetricasProductividadDto, ValidacionUrlDto, CategorizacionAutomaticaDto, AccesoRapidoDto } from '@models/accesoRapido/accesoRapidoDto.model';

@Injectable({
  providedIn: 'root'
})
export class AccesoRapidoService {

  private id!: number;

  set setId(id:number) {
    this.id = id;
  }

  get getId() {
    return this.id;
  }

  private readonly baseUrl = `/api/AccesoRapido`;

  constructor(private http: HttpClient) { }


  create(createAsuntoRequest:CreateAsuntoRequest):Observable<CreateResponseBase>
  {
    return  this.http.post<CreateResponseBase>("/api/Asunto/Create",createAsuntoRequest);
  }

  update(updateAsuntoRequest:UpdateAsuntoRequest):Observable<any>
  {
    return  this.http.put<any>("/api/Asunto/Update",updateAsuntoRequest);
  }

  getAll():Observable<AccesoRapidoDto[]>
  {
    return  this.http.get<AccesoRapidoDto[]>("/api/Asunto/GetAll");
  }



  getById(id:number):Observable<AccesoRapidoDto>
  {
    return  this.http.get<AccesoRapidoDto>("/api/Asunto/GetById?id="+id);
  }

  delete(id: number): Observable<any> {
    if (!isFalsy(id))
      return this.http.delete<any>("/api/Asunto/Delete?id=" + id);
    else
      return of(null)
  }

  getByUser(creadoPor: number, incluirInactivos: boolean = false): Observable<AccesoRapidoDto[]> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('incluirInactivos', incluirInactivos.toString());

    return this.http.get<AccesoRapidoDto[]>(`${this.baseUrl}/GetByUser`, { params });
  }

  buscar(filtros: FiltroAccesosRapidosRequest): Observable<AccesosRapidosPaginadosDto> {
    return this.http.post<AccesosRapidosPaginadosDto>(`${this.baseUrl}/Buscar`, filtros);
  }

  getFavoritos(creadoPor: number): Observable<AccesoRapidoSimpleDto[]> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.get<AccesoRapidoSimpleDto[]>(`${this.baseUrl}/GetFavoritos`, { params });
  }

  buscarTexto(creadoPor: number, searchTerm: string, limit?: number): Observable<AccesoRapidoSimpleDto[]> {
    let params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('searchTerm', searchTerm);

    if (limit) {
      params = params.set('limit', limit.toString());
    }

    return this.http.get<AccesoRapidoSimpleDto[]>(`${this.baseUrl}/BuscarTexto`, { params });
  }

  // #endregion

  // #region OPERACIONES DE FAVORITOS

  toggleFavorito(id: number, creadoPor: number): Observable<boolean> {
    const params = new HttpParams()
      .set('id', id.toString())
      .set('creadoPor', creadoPor.toString());

    return this.http.post<boolean>(`${this.baseUrl}/ToggleFavorito`, null, { params });
  }

  actualizarFavorito(request: ActualizarFavoritoRequest, creadoPor: number): Observable<boolean> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.put<boolean>(`${this.baseUrl}/ActualizarFavorito`, request, { params });
  }

  // #endregion

  // #region OPERACIONES DE VISITAS Y ESTADÍSTICAS

  registrarVisita(id: number, creadoPor: number, direccionIP?: string, userAgent?: string): Observable<boolean> {
    let params = new HttpParams()
      .set('id', id.toString())
      .set('creadoPor', creadoPor.toString());

    if (direccionIP) {
      params = params.set('direccionIP', direccionIP);
    }
    if (userAgent) {
      params = params.set('userAgent', userAgent);
    }

    return this.http.post<boolean>(`${this.baseUrl}/RegistrarVisita`, null, { params });
  }

  getEstadisticas(creadoPor: number): Observable<EstadisticasAccesosRapidosDto> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.get<EstadisticasAccesosRapidosDto>(`${this.baseUrl}/GetEstadisticas`, { params });
  }

  getTopAccesos(creadoPor: number, limite: number = 10, periodo: string = 'mes'): Observable<AccesoMasUsadoDto[]> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('limite', limite.toString())
      .set('periodo', periodo);

    return this.http.get<AccesoMasUsadoDto[]>(`${this.baseUrl}/GetTopAccesos`, { params });
  }

  // #endregion

  // #region OPERACIONES DE ORDENAMIENTO

  actualizarOrden(request: ActualizarOrdenRequest, creadoPor: number): Observable<boolean> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.put<boolean>(`${this.baseUrl}/ActualizarOrden`, request, { params });
  }

  reorganizar(creadoPor: number, criterio: string = 'uso', preservarFavoritos: boolean = true): Observable<boolean> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('criterio', criterio)
      .set('preservarFavoritos', preservarFavoritos.toString());

    return this.http.post<boolean>(`${this.baseUrl}/Reorganizar`, null, { params });
  }

  // #endregion

  // #region OPERACIONES DE GESTIÓN

  duplicar(id: number, creadoPor: number, nuevoTitulo?: string): Observable<AccesoRapidoDto> {
    let params = new HttpParams()
      .set('id', id.toString())
      .set('creadoPor', creadoPor.toString());

    if (nuevoTitulo) {
      params = params.set('nuevoTitulo', nuevoTitulo);
    }

    return this.http.post<AccesoRapidoDto>(`${this.baseUrl}/Duplicar`, null, { params });
  }

  // #endregion

  // #region OPERACIONES DE VERIFICACIÓN

  verificarUrl(id: number, creadoPor: number, timeoutSegundos: number = 10): Observable<VerificarUrlResponse> {
    const params = new HttpParams()
      .set('id', id.toString())
      .set('creadoPor', creadoPor.toString())
      .set('timeoutSegundos', timeoutSegundos.toString());

    return this.http.post<VerificarUrlResponse>(`${this.baseUrl}/VerificarUrl`, null, { params });
  }

  verificarUrlsMasivas(creadoPor: number, diasSinVerificar: number = 7): Observable<VerificacionMasivaDto> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('diasSinVerificar', diasSinVerificar.toString());

    return this.http.post<VerificacionMasivaDto>(`${this.baseUrl}/VerificarUrlsMasivas`, null, { params });
  }

  programarVerificacionAutomatica(creadoPor: number, habilitar: boolean = true): Observable<boolean> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('habilitar', habilitar.toString());

    return this.http.post<boolean>(`${this.baseUrl}/ProgramarVerificacionAutomatica`, null, { params });
  }

  // #endregion

  // #region OPERACIONES DE CONFIGURACIÓN

  getConfiguracion(creadoPor: number): Observable<ConfiguracionAccesosRapidosDto> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.get<ConfiguracionAccesosRapidosDto>(`${this.baseUrl}/GetConfiguracion`, { params });
  }

  actualizarConfiguracion(creadoPor: number, configuracion: ConfiguracionAccesosRapidosDto): Observable<boolean> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.put<boolean>(`${this.baseUrl}/ActualizarConfiguracion`, configuracion, { params });
  }

  restaurarConfiguracionDefault(creadoPor: number): Observable<boolean> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.post<boolean>(`${this.baseUrl}/RestaurarConfiguracionDefault`, null, { params });
  }

  // #endregion

  // #region OPERACIONES DE NOTIFICACIONES

  getNotificaciones(creadoPor: number): Observable<NotificacionAccesoRapidoDto[]> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.get<NotificacionAccesoRapidoDto[]>(`${this.baseUrl}/GetNotificaciones`, { params });
  }

  marcarNotificacionLeida(idNotificacion: number, creadoPor: number): Observable<boolean> {
    const params = new HttpParams()
      .set('idNotificacion', idNotificacion.toString())
      .set('creadoPor', creadoPor.toString());

    return this.http.post<boolean>(`${this.baseUrl}/MarcarNotificacionLeida`, null, { params });
  }

  // #endregion

  // #region OPERACIONES AVANZADAS

  getGruposConEstadisticas(creadoPor: number): Observable<GrupoConEstadisticasDto[]> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.get<GrupoConEstadisticasDto[]>(`${this.baseUrl}/GetGruposConEstadisticas`, { params });
  }

  moverGrupo(creadoPor: number, grupoOrigen: string, grupoDestino: string, confirmarCambios: boolean = true): Observable<boolean> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('grupoOrigen', grupoOrigen)
      .set('grupoDestino', grupoDestino)
      .set('confirmarCambios', confirmarCambios.toString());

    return this.http.post<boolean>(`${this.baseUrl}/MoverGrupo`, null, { params });
  }

  optimizarOrganizacion(creadoPor: number): Observable<OptimizacionResultadoDto> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.post<OptimizacionResultadoDto>(`${this.baseUrl}/OptimizarOrganizacion`, null, { params });
  }

  getSugerencias(creadoPor: number, limite: number = 10): Observable<SugerenciaAccesoRapidoDto[]> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('limite', limite.toString());

    return this.http.get<SugerenciaAccesoRapidoDto[]>(`${this.baseUrl}/GetSugerencias`, { params });
  }

  getSimilares(id: number, creadoPor: number, limite: number = 5): Observable<AccesoRapidoSimpleDto[]> {
    const params = new HttpParams()
      .set('id', id.toString())
      .set('creadoPor', creadoPor.toString())
      .set('limite', limite.toString());

    return this.http.get<AccesoRapidoSimpleDto[]>(`${this.baseUrl}/GetSimilares`, { params });
  }

  // #endregion

  // #region FUNCIONES FUTURAS (STUBS)

  sugerirReorganizacion(creadoPor: number): Observable<SugerenciaReorganizacionDto> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.post<SugerenciaReorganizacionDto>(`${this.baseUrl}/SugerirReorganizacion`, null, { params });
  }

  exportar(request: ExportarAccesosRapidosRequest): Observable<ExportacionResultadoDto> {
    return this.http.post<ExportacionResultadoDto>(`${this.baseUrl}/Exportar`, request);
  }

  importar(request: ImportarAccesosRapidosRequest): Observable<ResultadoImportacionDto> {
    return this.http.post<ResultadoImportacionDto>(`${this.baseUrl}/Importar`, request);
  }

  sincronizar(creadoPor: number, tipoServicio: string, configuracion: string): Observable<SincronizacionResultadoDto> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('tipoServicio', tipoServicio)
      .set('configuracion', configuracion);

    return this.http.post<SincronizacionResultadoDto>(`${this.baseUrl}/Sincronizar`, null, { params });
  }

  limpiarAccesosAntiguos(creadoPor: number, configuracion: LimpiezaConfiguracionDto): Observable<LimpiezaResultadoDto> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.post<LimpiezaResultadoDto>(`${this.baseUrl}/LimpiarAccesosAntiguos`, configuracion, { params });
  }

  optimizarDatos(creadoPor: number): Observable<OptimizacionDto> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.post<OptimizacionDto>(`${this.baseUrl}/OptimizarDatos`, null, { params });
  }

  crearBackup(creadoPor: number, incluirEstadisticas: boolean = true): Observable<BackupResultadoDto> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('incluirEstadisticas', incluirEstadisticas.toString());

    return this.http.post<BackupResultadoDto>(`${this.baseUrl}/CrearBackup`, null, { params });
  }

  generarReporteUso(creadoPor: number, fechaDesde: Date, fechaHasta: Date): Observable<ReporteUsoDto> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('fechaDesde', fechaDesde.toISOString().split('T')[0])
      .set('fechaHasta', fechaHasta.toISOString().split('T')[0]);

    return this.http.post<ReporteUsoDto>(`${this.baseUrl}/GenerarReporteUso`, null, { params });
  }

  analizarPatronesUso(creadoPor: number, diasAnalisis: number = 30): Observable<AnalisisPatronesDto> {
    const params = new HttpParams()
      .set('creadoPor', creadoPor.toString())
      .set('diasAnalisis', diasAnalisis.toString());

    return this.http.post<AnalisisPatronesDto>(`${this.baseUrl}/AnalizarPatronesUso`, null, { params });
  }

  getMetricasProductividad(creadoPor: number): Observable<MetricasProductividadDto> {
    const params = new HttpParams().set('creadoPor', creadoPor.toString());
    return this.http.get<MetricasProductividadDto>(`${this.baseUrl}/GetMetricasProductividad`, { params });
  }

  validarUrl(url: string, verificarAccesibilidad: boolean = true): Observable<ValidacionUrlDto> {
    const params = new HttpParams()
      .set('url', url)
      .set('verificarAccesibilidad', verificarAccesibilidad.toString());

    return this.http.post<ValidacionUrlDto>(`${this.baseUrl}/ValidarUrl`, null, { params });
  }

  sugerirTitulo(url: string): Observable<string> {
    const params = new HttpParams().set('url', url);
    return this.http.post<string>(`${this.baseUrl}/SugerirTitulo`, null, { params });
  }

  detectarCategoria(url: string, titulo?: string): Observable<CategorizacionAutomaticaDto> {
    let params = new HttpParams().set('url', url);
    if (titulo) {
      params = params.set('titulo', titulo);
    }

    return this.http.post<CategorizacionAutomaticaDto>(`${this.baseUrl}/DetectarCategoria`, null, { params });
  }

  verificarPermisos(id: number, creadoPor: number, operacion: string = 'read'): Observable<boolean> {
    const params = new HttpParams()
      .set('id', id.toString())
      .set('creadoPor', creadoPor.toString())
      .set('operacion', operacion);

    return this.http.get<boolean>(`${this.baseUrl}/VerificarPermisos`, { params });
  }

  // #endregion
}
